import { Inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BILLING_CONFIG } from './billing.config';
import { BillingConfig } from './billing.interface';

type Validation = boolean | undefined;

type UrlGetter = () => Promise<string>;

interface Style {
  [key: string]: Validation;
}

export enum CCFormFields {
  NUMBER = 'number',
  CCV = 'ccv',
  EXPIRY = 'expiry',
  NAME = 'name',
  ISSUING_CURRENCY_CODE = 'fx-issuing-currency', // For Native Charge
  POST_CODE = 'postcode', // For AVS for US users
  COUNTRY_CODE = 'ebanxCountryCode', // For Latin users
  EMAIL = 'ebanxEmail', // For Latin users
  PAYMENT_CURRENCY_CODE = 'ebanxCurrencyCode', // For Latin users - same as ISSUING_CURRENCY_CODE
  PHONE = 'ebanxPhone', // For Latin users
  CITY = 'ebanxCity', // For Latin users
  ADDRESS = 'ebanxAddress', // For Latin users
  STREET_COMPLEMENT = 'ebanxStreetComplement', // For Latin users
  STREET_NUMBER = 'ebanxStreetNo', // For Latin users
  ZIP = 'ebanxZip', // For Latin users
  STATE = 'ebanxState', // For Latin users
  DOCUMENT = 'ebanxDocument', // For Latin users
  BIRTH_DATE = 'ebanxBirthdate', // For Latin users
  RUT = 'chileanDocument',

  // For 3DS version 2
  BROWSER_INFO_SCREEN_WIDTH = 'browserScreenWidth',
  BROWSER_INFO_SCREEN_HEIGHT = 'browserScreenHeight',
  BROWSER_INFO_COLOUR_DEPTH = 'browserColourDepth',
  BROWSER_INFO_USER_AGENT = 'browserUserAgent',
  BROWSER_INFO_TZONE_OFFSET = 'browserTZoneOffset',
  BROWSER_INFO_LANGUAGE = 'browserLanguage',
  BROWSER_INFO_JAVA_ENABLED = 'browserJavaEnabled',
  BROWSER_INFO_JSCRIPT_ENABLED = 'browserJScriptEnabled',
}

export type CCFormExtraPayload = { [key in CCFormFields]?: string };

export interface Fields {
  [key: string]: HTMLIFrameElement;
  pan: HTMLIFrameElement;
  cvv: HTMLIFrameElement;
  name: HTMLIFrameElement;
  expiry: HTMLIFrameElement;
}

// Iframe of CVV for recurring payment
export interface CvvField {
  cvv: HTMLIFrameElement;
}

const slaveFields: Readonly<(keyof Fields)[]> = ['cvv', 'name', 'expiry'];

@Injectable({
  providedIn: 'root',
})
export class Billing {
  private origin: string;
  private url: UrlGetter;
  private fields: Fields;
  // FIXME: T267853 - enable that?
  // private callback: (e: MessageEvent) => void;
  private loadedFields = 0;
  private isIframeLoadingSubject$ = new BehaviorSubject<boolean>(true);
  private iframeUrlSubject$ = new BehaviorSubject<string>('');
  isIframeLoading$ = this.isIframeLoadingSubject$.asObservable();
  iframeUrl$ = this.iframeUrlSubject$.asObservable();

  constructor(@Inject(BILLING_CONFIG) private billingConfig: BillingConfig) {
    this.origin = this.billingConfig.baseUrl;
  }

  initialise(url: UrlGetter, fields: Fields): void {
    this.url = url;
    this.fields = fields;
    url().then(iframeUrl => this.iframeUrlSubject$.next(iframeUrl));

    fields.pan.addEventListener('load', () => {
      if (fields && fields.pan && fields.pan.contentWindow) {
        fields.pan.contentWindow.postMessage('pan', this.origin);
        slaveFields.forEach(slaveField => {
          const field = fields[slaveField];
          if (field && field.contentWindow) {
            field.contentWindow.postMessage('clear', this.origin);
          }
        });
      }
    });
    slaveFields.forEach(slaveField => {
      const field = fields[slaveField];
      if (field) {
        field.addEventListener('load', () => {
          if (field && field.contentWindow) {
            field.contentWindow.postMessage(slaveField, this.origin);
          }
        });
      }
    });
  }

  /**
   *  This function will send post message to billing to initialise CVV.
   *
   * @param field CVV iframe
   */
  initialiseCvv(field: CvvField): void {
    if (field) {
      field.cvv.addEventListener('load', () => {
        if (field && field.cvv.contentWindow) {
          field.cvv.contentWindow.postMessage('cvvOnly', this.origin);
        }
      });
    }
  }

  /**
   * This function will notify billing when user select different billing agreement.
   *
   * @param field CVV iframe
   */
  reloadCvv(field: CvvField): void {
    if (field) {
      if (field && field.cvv.contentWindow) {
        field.cvv.contentWindow.postMessage('reload', this.origin);
      }
    }
  }

  getMessageStream(): Observable<any> {
    return fromEvent<MessageEvent>(window, 'message').pipe(
      filter(m => m.origin === this.origin && typeof m.data === 'object'),
      map(m => m.data),
    );
  }

  /*
    async reload() {
      this.fields.pan.src = await this.url();
    }
  */

  // Same as the commented out function above without async await
  // as the powers the be have determined that we aren't using them.
  reload(): void {
    this.url().then(url => (this.fields.pan.src = url));
  }

  submit(amount: number, currencyCode: string, meta: CCFormExtraPayload): void {
    if (this.fields && this.fields.pan && this.fields.pan.contentWindow) {
      this.fields.pan.contentWindow.postMessage(
        { amount, currencyCode, meta },
        this.origin,
      );
    }
  }

  /**
   *  This function will send post message to generate billing data
   *  including encrypted CVV and browser info.
   *
   * @param field Iframe element for CVV
   * @param amount The deposit amount
   * @param currencyCode The deposit currency code
   * @param meta Extra data like browser info
   */
  submitCvv(
    field: CvvField,
    amount: number,
    currencyCode: string,
    meta: CCFormExtraPayload,
  ): void {
    if (field && field.cvv && field.cvv.contentWindow) {
      field.cvv.contentWindow.postMessage(
        { cvvSubmit: { amount, currencyCode, meta } },
        this.origin,
      );
    }
  }

  style(styling: Style, field: CvvField | null = null): void {
    Object.keys(styling).forEach(s => {
      const f = this.fields[s];
      if (f && f.src && f.contentWindow) {
        f.contentWindow.postMessage(styling, this.origin);
      }
    });

    // Send post message to update the CSS class for the validation
    if (field && field.cvv && field.cvv.src && field.cvv.contentWindow) {
      field.cvv.contentWindow.postMessage(styling, this.origin);
    }
  }

  /**
   * The function will check if all the iframes are successfully
   * loaded by onload callback function.
   *
   * @param iframes The ifremes for cc deposit
   */
  isIframeLoadingFinish(): Observable<boolean> {
    this.loadedFields = 0;
    const iframes = Object.values(this.fields);
    iframes.forEach(
      field =>
        (field.onload = () => {
          if (++this.loadedFields === iframes.length) {
            this.isIframeLoadingSubject$.next(false);
          }
        }),
    );
    return this.isIframeLoading$;
  }
}
